import { useEffect, useMemo } from "react";

import { jsonApiAdapter } from "../../common/helpers/jsonapi/jsonapi";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useCatalogueActions } from "../../contexts/CatalogueContext/CatalogueContext";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePreload } from "../../contexts/PreloadContext/PreloadProvider";
import { ICatalogue } from "./types";

const findProductInCatalogue = (
	itemList: ReadonlyArray<ICatalogue>,
	productId: string | number
): ICatalogue | undefined => {
	if (!productId) return undefined;

	return itemList.find((item) => item.id.toString() === productId.toString());
};

const enrichSetItemsWithBcrs = (catalogueData: ICatalogue[]): ICatalogue[] => {
	return catalogueData.map((product) => {
		if (!product.food_supplier_set_items?.length) {
			return product;
		}

		const enrichedSetItems = product.food_supplier_set_items.map((setItem) => {
			const referencedProduct = findProductInCatalogue(
				catalogueData,
				setItem.food_supplier_menu_item_id
			);

			if (referencedProduct && referencedProduct.bcrs) {
				return {
					...setItem,
					bcrs: referencedProduct.bcrs,
				};
			}

			return setItem;
		});

		return {
			...product,
			food_supplier_set_items: enrichedSetItems,
		};
	});
};

const HOST: string = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
const END_POINT: string = process.env.API_CATALOG ?? "";

const url: string = `${HOST}${END_POINT}`;

const useGetCatalogueData = () => {
	const { preloadImages } = usePreload();
	const { state: location } = useConfigContext((state) => state.location);
	const actions = useCatalogueActions();

	const payload = useMemo(
		() => ({
			url: location?.id ? url : "",
			triggerOnMount: true,
			body: {
				location: location?.id,
			},
			swrConfig: {
				revalidateOnFocus: false,
			},
		}),
		[url, location?.id]
	);
	const { data: response, isLoading, error } = useApiRequestPost(payload);

	useEffect(() => {
		if (!response) return;

		const data = jsonApiAdapter(response) as ICatalogue[];

		// Enrich setItems with bcrs information from referenced products
		const enrichedData = enrichSetItemsWithBcrs(data);

		const productImgs = enrichedData.map((item) => item.product_image);
		const productThumbnails = enrichedData.map((item) => item.product_thumbnail);

		preloadImages([...productImgs, ...productThumbnails]);

		actions.SET_CATALOGUE_DATA(enrichedData);
		actions.SET_ISLOADING(false);
	}, [response]);

	useEffect(() => {
		actions.SET_ISLOADING(isLoading);
		actions.SET_ERROR(error ? error.message : "");
	}, [isLoading, error]);
};

export default useGetCatalogueData;
